import * as api from './adapter';
import { apiRequest } from './adapter';

// CREW-1101 — the pay components a wage matrix is built from.
//
// Two endpoints with two shapes, on purpose:
//
//   catalogue    — JSON:API (`catalogs/wage_components`), the shared list of
//                  every component that exists, and where a new one is created.
//   owner set    — plain JSON (`crew_wage/owner_components`), because the set an
//                  owner HAS is not always a set that is stored: 50 of the 58
//                  owners with matrix rows have never been configured and get
//                  the seven built-in lines back. A JSON:API index over the
//                  join table would answer "no columns" for them.
const OWNER_SET_URL = '/api/crew_wage/owner_components';
const MATRIX_RANKS_URL = '/api/crew_wage/matrix_ranks';

// `apiRequest(url, options, headers)` — the headers go in the THIRD argument,
// never inside `options`. It merges the two shallowly (`{...defaultOptions,
// ...options}`), so an `options.headers` replaces the whole default set and
// takes the devise-token-auth credentials with it: every call comes back 401.
const jsonOptions = (method, body) => [
  {
    method,
    ...(body ? { body: JSON.stringify(body) } : {}),
  },
  { 'Content-Type': 'application/json' },
];

export default {
  getCatalog: () => api.read('catalogs/wage_components'),

  createComponent: (attributes) => api.create({
    type: 'wage_components', controller: 'catalogs/wage_components', attributes,
  }),

  // → { data: [{ id, code, title, short_title, kind, position, built_in }], configured }
  getOwnerSet: (ownerId) => api.read('crew_wage/owner_components', {
    query: { owner_id: ownerId },
  }),

  addToOwner: (ownerId, componentId) => apiRequest(
    OWNER_SET_URL,
    ...jsonOptions('POST', { owner_id: ownerId, component_id: componentId }),
  ),

  // Where the component's figure lands on this owner's contract template.
  // An empty string clears the override; for one of the seven original lines
  // that means falling back to the cell they have always used, and for an
  // added one it means "not printed".
  setPrintCell: (ownerId, componentId, printCell) => apiRequest(
    `${OWNER_SET_URL}/${componentId}`,
    ...jsonOptions('PATCH', { owner_id: ownerId, print_cell: printCell }),
  ),

  // `:id` is the COMPONENT's id — the dialog has the column in hand, never the
  // membership row.
  removeFromOwner: (ownerId, componentId) => apiRequest(
    `${OWNER_SET_URL}/${componentId}?owner_id=${ownerId}`,
    ...jsonOptions('DELETE'),
  ),

  // The matrix's ROWS — the Ranks tab of the same dialog. Plain JSON for the
  // same reason as the owner set above: what comes back is not the `roles`
  // table but an answer about this owner, one entry per rank his matrix has
  // cards for, carrying whether it may be dropped and why not.
  //
  // → { data: [{ role_id, title, manned, has_figures, removable }] }
  //
  // CREW-1299 — `vesselId` addresses a vessel's own matrix instead; the
  // owner is then the vessel's and need not be sent.
  getMatrixRanks: (ownerId, vesselId = null) => api.read('crew_wage/matrix_ranks', {
    query: vesselId ? { vessel_id: vesselId } : { owner_id: ownerId },
  }),

  // The whole ticked set, not a diff. One refused rank refuses the request
  // and nothing is written, so the grid never ends up half-applied.
  applyMatrixRanks: (ownerId, roleIds, vesselId = null) => apiRequest(
    `${MATRIX_RANKS_URL}/apply`,
    ...jsonOptions('PUT', {
      ...(vesselId ? { vessel_id: vesselId } : { owner_id: ownerId }),
      role_ids: roleIds,
    }),
  ),
};
